import React from "react";
import { motion } from "framer-motion";
import { MessageCircle, Sparkles } from "lucide-react";
import { buildWhatsAppLink } from "../data/menu";

const bgMap = {
  yellow: {
    card: "bg-[#FDF8E1]",
    plate: "bg-gradient-to-br from-[#F7E9A8] to-[#FFF6CE]",
    tag: "bg-[#3E2723] text-[#FDF8E1]",
  },
  cream: {
    card: "bg-[#FFFDF9]",
    plate: "bg-gradient-to-br from-[#FCE4EA] to-[#FFE9D6]",
    tag: "bg-[#D38C41] text-white",
  },
  green: {
    card: "bg-[#E6EDE5]",
    plate: "bg-gradient-to-br from-[#CFE0CB] to-[#EFF4ED]",
    tag: "bg-[#3E2723] text-[#E6EDE5]",
  },
};

export const MenuCard = ({ item, index }) => {
  const palette = bgMap[item.bg] || bgMap.cream;

  return (
    <motion.article
      data-testid={`menu-card-${item.id}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay: (index % 3) * 0.08 }}
      whileHover={{ y: -8 }}
      className={`group relative ${palette.card} rounded-[2.5rem] p-6 md:p-7 overflow-hidden border border-[#3E2723]/5 shadow-[0_20px_40px_rgba(62,39,35,0.06)] hover:shadow-[0_30px_60px_rgba(62,39,35,0.12)] transition-shadow duration-500`}
    >
      {/* Floating tag */}
      <div
        className={`absolute top-5 right-5 z-10 ${palette.tag} text-[10px] font-extrabold uppercase tracking-widest px-3 py-1.5 rounded-full flex items-center gap-1`}
      >
        <Sparkles size={11} />
        {item.tag}
      </div>

      {/* Plate / image */}
      <div
        className={`relative aspect-square w-full rounded-[2rem] ${palette.plate} mb-6 overflow-hidden grid place-items-center`}
      >
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_30%_30%,white,transparent_60%)]" />
        <motion.img
          src={item.image}
          alt={item.title}
          loading="lazy"
          draggable="false"
          whileHover={{ scale: 1.07, rotate: -2 }}
          transition={{ type: "spring", stiffness: 120, damping: 14 }}
          className="relative z-10 w-[92%] h-[92%] object-contain drop-shadow-[0_28px_30px_rgba(62,39,35,0.25)] select-none"
          data-testid={`menu-image-${item.id}`}
        />
      </div>

      {/* Text content */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <h3
          className="font-display font-extrabold text-[1.45rem] md:text-2xl leading-tight text-[#3E2723]"
          data-testid={`menu-title-${item.id}`}
        >
          {item.title}
        </h3>
        <div
          className="font-display font-black text-xl md:text-2xl text-[#D38C41] whitespace-nowrap"
          data-testid={`menu-price-${item.id}`}
        >
          {item.price}
        </div>
      </div>

      <p
        className="text-sm text-[#5D4037] leading-relaxed font-body mb-5"
        data-testid={`menu-desc-${item.id}`}
      >
        {item.description}
      </p>

      <a
        href={buildWhatsAppLink(item.title)}
        target="_blank"
        rel="noreferrer"
        data-testid={`menu-order-${item.id}`}
        className="inline-flex items-center justify-center gap-2 w-full rounded-full bg-[#25D366] hover:bg-[#1fb957] text-white px-5 py-3.5 font-bold buffy-link shadow-[0_12px_24px_rgba(37,211,102,0.35)]"
      >
        <MessageCircle size={17} />
        Pedir por WhatsApp
      </a>
    </motion.article>
  );
};

export default MenuCard;
